import { createClient } from '@supabase/supabase-js'
import NetworkService from '../services/NetworkService';

// Supabase project configuration
// You'll get these values when you create your Supabase project at https://supabase.com
// Updated: 2024 - Ensuring all components use this configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Get the current site URL
const getSiteUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return 'https://scenefeed.live'
  }
  return window.location.origin
}

// Create Supabase client with custom type handling
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  db: {
    schema: 'public'
  },
  auth: {
    flowType: 'pkce',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    redirectTo: `${getSiteUrl()}/auth/callback`,
    transformUser: (user) => {
      if (user?.user_metadata?.user_type) {
        const userType = user.user_metadata.user_type.toLowerCase();
        if (['fan', 'band', 'venue'].includes(userType)) {
          user.user_metadata.user_type = userType;
        }
      }
      return user;
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'scene-feed-pwa'
    }
  }
})

// Enhanced query wrapper with retry logic
export const executeWithRetry = async (operation, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  console.log(`🔄 executeWithRetry: Starting with ${maxRetries} max retries, ${baseDelay}ms base delay`);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 executeWithRetry: Attempt ${attempt}/${maxRetries}`);
      
      // Check network status before attempting
      if (!NetworkService.isOnline) {
        console.log('❌ executeWithRetry: Network offline, throwing error');
        throw new Error('Network offline');
      }

      console.log(`🔄 executeWithRetry: Executing operation (attempt ${attempt})`);
      const result = await operation();
      console.log(`✅ executeWithRetry: Operation successful on attempt ${attempt}`);
      return result;
    } catch (error) {
      lastError = error;
      console.warn(`⚠️ executeWithRetry: Attempt ${attempt} failed:`, error.message);
      
      // Don't retry on certain errors
      if (error.message?.includes('Invalid login credentials') ||
          error.message?.includes('Email not confirmed') ||
          error.message?.includes('User not found')) {
        console.log(`❌ executeWithRetry: Non-retryable error, throwing immediately`);
        throw error;
      }
      
      // If it's the last attempt, throw the error
      if (attempt === maxRetries) {
        console.log(`❌ executeWithRetry: Max retries reached, throwing error`);
        throw error;
      }
      
      // Exponential backoff
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`⏳ executeWithRetry: Waiting ${delay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  console.log(`❌ executeWithRetry: All attempts failed, throwing last error`);
  throw lastError;
};

// Database table names
export const TABLES = {
  USERS: 'users',
  SHOWS: 'shows', 
  ADVERTISEMENTS: 'advertisements',
  APPROVED_ADVERTISERS: 'approved_advertisers',
  ANALYTICS: 'analytics',
  SHOW_LIKES: 'show_likes',
  NOTIFICATIONS: 'notifications'
}

// Helper function to check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  return supabaseUrl && supabaseAnonKey
}

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
};

// Helper function to get user session
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

export default supabase 